import {
  Alert as MuiAlert,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { PageHeader } from '@opengov/components-page-header';
import { ChevronDown, ChevronRight } from '@opengov/react-capital-assets';

import { SectionCard } from '../components/vcm/SectionCard';
import { TierBadge } from '../components/vcm/TierBadge';
import { AVAILABLE_EVENTS, useVcm } from '../contexts/VcmContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { calculateAge, formatDob } from '../lib/vcm/computed';
import { TEXAS_COUNTIES } from '../lib/vcm/counties';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetClients } from '../mocks/vcm-mock-data';
import type { StructuredAddress, TcrClient } from '../types/vcm';

const STEPS = ['Registration History', 'Duplicate Search', 'Intake', 'Contact & Review'] as const;

type PriorRegistration = '' | 'Yes' | 'No' | 'Unsure';

const GENDER_OPTIONS = ['Female', 'Male', 'Non-binary', 'Prefer not to say', 'Other'] as const;
const LANGUAGE_OPTIONS = ['English', 'Spanish', 'Vietnamese', 'Mandarin', 'Other'] as const;

interface FormState {
  firstName: string;
  middleName: string;
  lastName: string;
  nickname: string;
  dateOfBirth: string;
  gender: string;
  preferredLanguage: string;
  phoneHome: string;
  email: string;
  address1: string;
  city: string;
  county: string;
  zip: string;
  event: string;
  intakeOrganization: string;
  dateOfInitialIntake: string;
}

const EMPTY_FORM: FormState = {
  firstName: '',
  middleName: '',
  lastName: '',
  nickname: '',
  dateOfBirth: '',
  gender: '',
  preferredLanguage: '',
  phoneHome: '',
  email: '',
  address1: '',
  city: '',
  county: '',
  zip: '',
  event: AVAILABLE_EVENTS[0]?.programCode ?? '',
  intakeOrganization: '',
  dateOfInitialIntake: '',
};

/** Digits-only phone for consistent matching. */
function digits(value: string | null): string {
  return (value ?? '').replace(/\D/g, '');
}

/** Birth month/year key (YYYY-MM) for fuzzy duplicate matching. */
function birthKey(dob: string | null): string {
  return (dob ?? '').slice(0, 7);
}

function formatAddress(addr: StructuredAddress | null): string {
  if (!addr) return '—';
  const parts = [addr.address1, addr.address2, addr.city, addr.state, addr.zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

export function NewClientPage() {
  const { navigateToPath } = useAppNavigation();
  const { setSaveStatus, availableEvents } = useVcm();
  const [searchParams] = useSearchParams();

  // Pre-fill from call center if params present
  const fromCallCenter = searchParams.get('source') === 'call-center';
  const callCenterSourceId = searchParams.get('sourceId') ?? '';
  const prefilled: FormState = fromCallCenter ? {
    ...EMPTY_FORM,
    firstName: searchParams.get('firstName') ?? '',
    lastName: searchParams.get('lastName') ?? '',
    phoneHome: searchParams.get('phone') ?? '',
    email: searchParams.get('email') ?? '',
    dateOfBirth: searchParams.get('dob') ?? '',
    gender: searchParams.get('gender') ?? '',
    preferredLanguage: searchParams.get('language') ?? '',
    address1: searchParams.get('address') ?? '',
    city: searchParams.get('city') ?? '',
    zip: searchParams.get('zip') ?? '',
  } : EMPTY_FORM;

  const [activeStep, setActiveStep] = useState(fromCallCenter ? 2 : 0);
  const [priorRegistration, setPriorRegistration] = useState<PriorRegistration>(fromCallCenter ? 'No' : '');
  const [form, setForm] = useState<FormState>(prefilled);
  const [tcrIdQuery, setTcrIdQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setField = (key: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const selectedEventName =
    availableEvents.find((e) => e.programCode === form.event)?.name ?? 'event';

  /* MOCK DATA — auto-match on (last name + birth month/year) OR phone. */
  const autoMatches = useMemo(() => {
    const last = form.lastName.toLowerCase().trim();
    const bKey = birthKey(form.dateOfBirth);
    const phone = digits(form.phoneHome);
    if (!last && !phone) return [];
    return mockGetClients().filter((c) => {
      // Skip the source record when converting from call center
      if (callCenterSourceId && c.id === callCenterSourceId) return false;
      const nameDobMatch =
        Boolean(last) &&
        c.lastName.toLowerCase() === last &&
        Boolean(bKey) &&
        birthKey(c.dateOfBirth) === bKey;
      const phoneMatch = Boolean(phone) && digits(c.phoneHome) === phone;
      return nameDobMatch || phoneMatch;
    });
  }, [form.lastName, form.dateOfBirth, form.phoneHome, callCenterSourceId]);

  /* MOCK DATA — full duplicate search query incl. TCRID lookup (VCM-XXXXX). */
  const duplicates = useMemo(() => {
    const tcrId = tcrIdQuery.trim().toLowerCase();
    if (autoMatches.length === 0 && !tcrId) return [];
    const byId = tcrId
      ? mockGetClients().filter((c) => c.id.toLowerCase().includes(tcrId))
      : [];
    const merged = new Map<string, TcrClient>();
    for (const c of [...autoMatches, ...byId]) merged.set(c.id, c);
    return [...merged.values()];
  }, [autoMatches, tcrIdQuery]);

  const validateIdentity = (): boolean => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First and last name are required.');
      return false;
    }
    if (!form.dateOfBirth) {
      setError('Date of birth is required.');
      return false;
    }
    if (!form.phoneHome.trim()) {
      setError('Phone number is required.');
      return false;
    }
    if (!form.address1.trim()) {
      setError('Address is required.');
      return false;
    }
    setError(null);
    return true;
  };

  const goNext = () => {
    if (activeStep === 0) {
      if (!priorRegistration) {
        setError('Please choose whether this person has registered before.');
        return;
      }
      setError(null);
      // "No" skips the duplicate search and goes straight to intake.
      setActiveStep(priorRegistration === 'No' ? 2 : 1);
      return;
    }
    if (activeStep === 2 && !validateIdentity()) return;
    setError(null);
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setError(null);
    // Returning from intake on the "No" path goes back to the first question.
    if (activeStep === 2 && priorRegistration === 'No') {
      setActiveStep(0);
      return;
    }
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const skipToIntake = () => {
    setError(null);
    setActiveStep(2);
  };

  const handleSubmit = () => {
    if (!validateIdentity()) return;
    /* MOCK SAVE — replace with real /v2 create call */
    setSaveStatus('saving');
    window.setTimeout(() => {
      setSaveStatus('saved');
      navigateToPath('clients/search');
    }, 600);
  };

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader
        title="New Client Record"
        description="Create a new client after checking for duplicates."
      />

      <SectionCard title="Progress">
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </SectionCard>

      {error && <MuiAlert severity="error">{error}</MuiAlert>}

      {activeStep === 0 && (
        <SectionCard title="Registration History">
          <Stack spacing={2}>
            <Typography variant="body2">Has this person registered before?</Typography>
            <ToggleButtonGroup
              exclusive
              value={priorRegistration}
              onChange={(_, value: PriorRegistration | null) => {
                if (value) setPriorRegistration(value);
              }}
              size="small"
              aria-label="Prior registration"
            >
              <ToggleButton value="Yes">Yes</ToggleButton>
              <ToggleButton value="No">No</ToggleButton>
              <ToggleButton value="Unsure">Unsure</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: (theme) => theme.palette.text.secondary }}>
              Choose “No” to go straight to intake. “Yes” or “Unsure” opens a duplicate search
              first.
            </Typography>
          </Stack>
        </SectionCard>
      )}

      {activeStep === 1 && (
        <SectionCard title="Check for Existing Client">
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="First Name"
                  fullWidth
                  size="small"
                  value={form.firstName}
                  onChange={(e) => setField('firstName', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Last Name"
                  fullWidth
                  size="small"
                  value={form.lastName}
                  onChange={(e) => setField('lastName', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Date of Birth" required
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={form.dateOfBirth}
                  onChange={(e) => setField('dateOfBirth', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Home Phone" required
                  fullWidth
                  size="small"
                  value={form.phoneHome}
                  onChange={(e) => setField('phoneHome', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="VCM ID"
                  placeholder="VCM-XXXXX"
                  fullWidth
                  size="small"
                  value={tcrIdQuery}
                  onChange={(e) => setTcrIdQuery(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Event"
                  fullWidth
                  size="small"
                  value={form.event}
                  onChange={(e) => setField('event', e.target.value)}
                >
                  {availableEvents.map((ev) => (
                    <MenuItem key={ev.programCode} value={ev.programCode}>
                      {ev.name} ({ev.programCode})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {duplicates.length > 0 ? (
              <Box>
                <MuiAlert severity="warning" sx={{ mb: (theme) => theme.spacing(1) }}>
                  {duplicates.length} possible match{duplicates.length !== 1 ? 'es' : ''} found
                  (matched on last name + birth month/year, phone, or VCM ID). Add the client to
                  this event instead of creating a duplicate.
                </MuiAlert>
                <Stack spacing={1}>
                  {duplicates.map((c) => {
                    const expanded = expandedId === c.id;
                    return (
                      <Box
                        key={c.id}
                        sx={{
                          p: (theme) => theme.spacing(1.5),
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ sm: 'center' }}
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {c.firstName} {c.lastName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: (theme) => theme.palette.text.secondary }}
                            >
                              {c.id} &middot; DOB {formatDob(c.dateOfBirth)} &middot;{' '}
                              {c.phoneHome ?? '—'} &middot; {c.county} County
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              size="small"
                              variant="text"
                              startIcon={expanded ? <ChevronDown /> : <ChevronRight />}
                              onClick={() => setExpandedId(expanded ? null : c.id)}
                            >
                              {expanded ? 'Hide' : 'View details'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigateToPath(`clients/${c.id}`)}
                            >
                              Add to {selectedEventName}
                            </Button>
                          </Stack>
                        </Stack>

                        {expanded && (
                          <Box sx={{ mt: (theme) => theme.spacing(1.5) }}>
                            <Divider sx={{ mb: (theme) => theme.spacing(1.5) }} />
                            <Grid container spacing={1}>
                              <DetailItem
                                label="Age"
                                value={c.dateOfBirth ? `${calculateAge(c.dateOfBirth)}` : '—'}
                              />
                              <DetailItem label="Gender" value={c.gender ?? '—'} />
                              <DetailItem label="Language" value={c.preferredLanguage ?? '—'} />
                              <DetailItem
                                label="Primary Address"
                                value={formatAddress(c.primaryAddress)}
                              />
                              <DetailItem
                                label="Mailing Address"
                                value={formatAddress(c.mailingAddress)}
                              />
                              <DetailItem label="Open Cases" value={`${c.openCases}`} />
                              <DetailItem label="Manager" value={c.manager ?? '—'} />
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      color: (theme) => theme.palette.text.secondary,
                                    }}
                                  >
                                    Tier
                                  </Typography>
                                  <TierBadge tier={c.tier} />
                                </Stack>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
                <Button
                  variant="text"
                  size="small"
                  sx={{ mt: (theme) => theme.spacing(1) }}
                  onClick={skipToIntake}
                >
                  Skip and create new record
                </Button>
              </Box>
            ) : (
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                  No matching clients found. You can continue creating a new record.
                </Typography>
                <Button variant="outlined" size="small" onClick={skipToIntake}>
                  Proceed to new record
                </Button>
              </Stack>
            )}
          </Stack>
        </SectionCard>
      )}

      {activeStep === 2 && (
        <SectionCard title="Intake">
          <Stack spacing={2}>
            {autoMatches.length > 0 && (
              <MuiAlert severity="warning">
                {autoMatches.length} possible duplicate{autoMatches.length !== 1 ? 's' : ''}{' '}
                detected (last name + date of birth or phone). Confirm this is a new client before
                continuing.
              </MuiAlert>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="First Name"
                  required
                  fullWidth
                  size="small"
                  value={form.firstName}
                  onChange={(e) => setField('firstName', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Middle Name"
                  fullWidth
                  size="small"
                  value={form.middleName}
                  onChange={(e) => setField('middleName', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Last Name"
                  required
                  fullWidth
                  size="small"
                  value={form.lastName}
                  onChange={(e) => setField('lastName', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Nickname"
                  fullWidth
                  size="small"
                  value={form.nickname}
                  onChange={(e) => setField('nickname', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Date of Birth" required
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={form.dateOfBirth}
                  onChange={(e) => setField('dateOfBirth', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  label="Gender"
                  fullWidth
                  size="small"
                  value={form.gender}
                  onChange={(e) => setField('gender', e.target.value)}
                >
                  <MenuItem value="">Select gender</MenuItem>
                  {GENDER_OPTIONS.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  label="Preferred Language"
                  fullWidth
                  size="small"
                  value={form.preferredLanguage}
                  onChange={(e) => setField('preferredLanguage', e.target.value)}
                >
                  <MenuItem value="">Select language</MenuItem>
                  {LANGUAGE_OPTIONS.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary }}
            >
              Primary Address
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Address 1" required
                  fullWidth
                  size="small"
                  value={form.address1}
                  onChange={(e) => setField('address1', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="City"
                  fullWidth
                  size="small"
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="County"
                  fullWidth
                  size="small"
                  value={form.county}
                  onChange={(e) => setField('county', e.target.value)}
                >
                  <MenuItem value="">Select a county</MenuItem>
                  {TEXAS_COUNTIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="ZIP"
                  fullWidth
                  size="small"
                  value={form.zip}
                  onChange={(e) => setField('zip', e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary }}
            >
              Event & Intake
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  label="Event Assignment"
                  fullWidth
                  size="small"
                  value={form.event}
                  onChange={(e) => setField('event', e.target.value)}
                >
                  {availableEvents.map((ev) => (
                    <MenuItem key={ev.programCode} value={ev.programCode}>
                      {ev.name} ({ev.programCode})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Intake Organization"
                  fullWidth
                  size="small"
                  value={form.intakeOrganization}
                  onChange={(e) => setField('intakeOrganization', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Date of Initial Intake"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={form.dateOfInitialIntake}
                  onChange={(e) => setField('dateOfInitialIntake', e.target.value)}
                />
              </Grid>
            </Grid>
          </Stack>
        </SectionCard>
      )}

      {activeStep === 3 && (
        <SectionCard title="Contact & Review">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Home Phone" required
                fullWidth
                size="small"
                value={form.phoneHome}
                onChange={(e) => setField('phoneHome', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                size="small"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary }}
              >
                Review
              </Typography>
              <Typography variant="body2">
                {form.firstName} {form.lastName}
                {form.dateOfBirth ? ` \u00b7 DOB ${formatDob(form.dateOfBirth)}` : ''}
                {form.county ? ` \u00b7 ${form.county} County` : ''}
                {` \u00b7 ${selectedEventName}`}
                {form.intakeOrganization ? ` \u00b7 ${form.intakeOrganization}` : ''}
              </Typography>
            </Grid>
          </Grid>
        </SectionCard>
      )}


      <Stack direction="row" justifyContent="space-between">
        <Button variant="text" disabled={activeStep === 0} onClick={goBack}>
          Back
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" onClick={goNext}>
            Continue
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit}>
            Create Client
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function DetailItem({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  );
}
